import { HttpClient } from '@/shared';
import {
  pageQueryString,
  toPagedItems,
  type PagedItems,
  type PageQuery,
} from '@/shared/application/pagination/pagedItems';
import { X_API_KEY } from '@/shared/infra/http/runtimeEnv';
import { getEnvironmentConfig } from '@/platform/application/config/environment';
import {
  AnalyzeParams,
  AssignBatchParams,
  BundleCorrection,
  ExtractOptions,
  IBatchProcessingRepository,
  VendorDelivery,
  VendorImportParams,
  VendorReconcileParams,
  VendorWorksitePreview,
} from '../../application/ports/outbound/IBatchProcessingRepository';
import {
  DigitizationBatch,
  DigitizationBatchSummary,
  DigitizationBundle,
  DigitizationInProgressQueueItem,
  BatchLocation,
  InternalUser,
  OpsReviewQueueItem,
  ReverificationGapsResult,
  PublishBatchResult,
  PublishBundleResult,
  ReviewAuditRecord,
  StreamEvent,
  ConfirmAllResult,
  ReExtractAllProgress,
  ReExtractAllResult,
  ReExtractAllTotals,
  RecountFindingsProgress,
  RecountFindingsResult,
  RecountFindingsTotals,
  FollowingRecord,
} from '../../application/domain/entities/Digitization';

/**
 * Calls the core-service digitization endpoints. CRUD uses the shared axios HttpClient; the two
 * streaming endpoints (analyze, extract) use native fetch with `credentials: 'include'` (cookie
 * auth) because the axios wrapper buffers the full body and can't read NDJSON incrementally.
 */
export class ApiBatchProcessingRepository implements IBatchProcessingRepository {
  private readonly baseUrl: string;

  constructor(private readonly httpClient: HttpClient) {
    // Trim a trailing slash so we can append paths cleanly.
    this.baseUrl = getEnvironmentConfig().apiBaseUrlCore.replace(/\/$/, '');
  }

  getPdfUrl(batchId: string): string {
    return `${this.baseUrl}/digitization/batch/pdf/${batchId}`;
  }

  /** Read a fetch Response body as a newline-delimited JSON stream. */
  private async readNdjson(res: Response, onEvent: (e: StreamEvent) => void): Promise<void> {
    if (!res.body) throw new Error(`Stream failed: HTTP ${res.status}`);
    if (!res.ok && res.status >= 400) {
      // Non-streamed error (e.g. 401/403/404) — surface as an error event.
      const text = await res.text().catch(() => '');
      onEvent({ type: 'error', message: text || `HTTP ${res.status}` });
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
          onEvent(JSON.parse(trimmed) as StreamEvent);
        } catch {
          /* ignore partial / non-JSON keepalive lines */
        }
      }
    }
    const tail = buffer.trim();
    if (tail) {
      try {
        onEvent(JSON.parse(tail) as StreamEvent);
      } catch {
        /* ignore */
      }
    }
  }

  async analyzeBatch(params: AnalyzeParams, onEvent: (e: StreamEvent) => void): Promise<void> {
    const form = new FormData();
    form.append('file', params.file);
    if (params.model) form.append('model', params.model);
    form.append('includeRemediation', String(params.includeRemediation ?? false));

    const res = await fetch(`${this.baseUrl}/digitization/batch`, {
      method: 'POST',
      body: form,
      credentials: 'include',
      headers: { 'X-Api-Key': X_API_KEY },
    });
    await this.readNdjson(res, onEvent);
  }

  /**
   * VTK-1991 — start a vendor-export import. Only the two manifests travel in the request; the
   * documents were placed in the container beforehand and are named by `documentPath`.
   */
  async importVendorExport(
    params: VendorImportParams,
    onEvent: (e: StreamEvent) => void
  ): Promise<void> {
    const res = await fetch(`${this.baseUrl}/digitization/batch/vendor-import`, {
      method: 'POST',
      body: JSON.stringify(params),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': X_API_KEY },
    });
    await this.readNdjson(res, onEvent);
  }

  /** VTK-2153 — reconcile the client's imported cases with every delivery held for them. */
  async reconcileVendorImports(
    params: VendorReconcileParams,
    onEvent: (e: StreamEvent) => void
  ): Promise<void> {
    const res = await fetch(`${this.baseUrl}/digitization/batch/vendor-import/reconcile`, {
      method: 'POST',
      body: JSON.stringify(params),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': X_API_KEY },
    });
    await this.readNdjson(res, onEvent);
  }

  /**
   * VTK-1991 — what is actually in the container, so the operator picks a delivery rather than
   * typing its path. A typed path is what made the first real run fail: the field's default prefix
   * was submitted unchanged and the import looked for a manifest one level up.
   */
  async listVendorDeliveries(path?: string): Promise<VendorDelivery[]> {
    const qs = path ? `?path=${encodeURIComponent(path)}` : '';
    const res = await this.httpClient.get<VendorDelivery[]>(
      `/digitization/batch/vendor-import/deliveries${qs}`
    );
    return res.data ?? [];
  }

  /**
   * VTK-1991 — the worksites in a delivery. Nothing imports until every code resolves to one of the
   * client's locations, and this is what replaced doing that mapping with hand-written SQL.
   */
  async previewVendorWorksites(documentPath: string): Promise<VendorWorksitePreview> {
    const res = await this.httpClient.get<VendorWorksitePreview>(
      `/digitization/batch/vendor-import/worksites?documentPath=${encodeURIComponent(documentPath)}`
    );
    return res.data ?? { locations: [], worksites: [] };
  }

  async saveVendorWorksites(
    worksites: Array<{ code: string; label?: string | null; locationId?: string | null }>
  ): Promise<void> {
    await this.httpClient.put('/digitization/batch/vendor-import/worksites', { worksites });
  }

  async extractBatch(
    batchId: string,
    onEvent: (e: StreamEvent) => void,
    options: ExtractOptions = {}
  ): Promise<void> {
    const params = new URLSearchParams();
    if (options.skipAi) params.set('skipAi', 'true');
    if (options.force) params.set('force', 'true');
    if (options.skipRules) params.set('skipRules', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`${this.baseUrl}/digitization/batch/${batchId}/extract${qs}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Api-Key': X_API_KEY },
    });
    await this.readNdjson(res, onEvent);
  }

  async revalidateBatch(
    batchId: string
  ): Promise<{ batch: DigitizationBatch; bundles: DigitizationBundle[] }> {
    const res = await this.httpClient.post<{
      batch: DigitizationBatch;
      bundles: DigitizationBundle[];
    }>(`/digitization/batch/${batchId}/revalidate`, {});
    return res.data;
  }

  async listBatches(
    page: PageQuery,
    options?: { excludeCompleted?: boolean }
  ): Promise<PagedItems<DigitizationBatchSummary>> {
    const exclude = options?.excludeCompleted ? '&excludeCompleted=true' : '';
    const res = await this.httpClient.get<
      PagedItems<DigitizationBatchSummary> | DigitizationBatchSummary[]
    >(`/digitization/batch?${pageQueryString(page)}${exclude}`);
    return toPagedItems(res.data, page);
  }

  async listFollowingRecords(
    batchId: string,
    page: PageQuery
  ): Promise<PagedItems<FollowingRecord>> {
    const res = await this.httpClient.get<PagedItems<FollowingRecord>>(
      `/digitization/batch/${batchId}/following-records?${pageQueryString(page)}`
    );
    return toPagedItems(res.data, page);
  }

  async getBatch(
    batchId: string
  ): Promise<{ batch: DigitizationBatch; bundles: DigitizationBundle[] }> {
    const res = await this.httpClient.get<{
      batch: DigitizationBatch;
      bundles: DigitizationBundle[];
    }>(`/digitization/batch/${batchId}`);
    return res.data;
  }

  async patchBundle(
    batchId: string,
    bundleId: string,
    correction: BundleCorrection
  ): Promise<PublishBundleResult> {
    // Completing a record auto-publishes it and the endpoint answers 200 either way — the real outcome
    // is in `publishResult` (VTK-1930). Same reason `publishBundle` below reads its body instead of
    // treating a resolved request as a successful publish.
    const res = await this.httpClient.patch<PublishBundleResult>(
      `/digitization/batch/${batchId}/bundles/${bundleId}`,
      correction
    );
    return res.data;
  }

  async publishBatch(batchId: string): Promise<PublishBatchResult> {
    const res = await this.httpClient.post<PublishBatchResult>(
      `/digitization/batch/${batchId}/publish`,
      {}
    );
    return res.data;
  }

  /**
   * Streamed rather than posted through axios: every record is also published, so a real import runs
   * well past the shared client's request timeout, which aborted it while the server kept working.
   */
  async confirmAllBundles(
    batchId: string,
    reason: string,
    onProgress?: (completed: number, total: number) => void
  ): Promise<ConfirmAllResult> {
    const res = await fetch(`${this.baseUrl}/digitization/batch/${batchId}/confirm-all`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': X_API_KEY },
    });
    const outcome: { result?: ConfirmAllResult; error?: string } = {};
    await this.readNdjson(res, (e) => {
      if (e.type === 'progress' && e.completed != null && e.total != null) {
        onProgress?.(e.completed, e.total);
      } else if (e.type === 'confirm_result') {
        outcome.result = e.result;
      } else if (e.type === 'error') {
        outcome.error = e.message;
      }
    });
    if (outcome.error) throw new Error(outcome.error);
    if (!outcome.result) {
      throw new Error(
        'The connection closed before confirming finished. Refresh to see how far it got.'
      );
    }
    return outcome.result;
  }

  /**
   * Re-extract every batch in the account (VTK-2073). Streamed: one event per batch, so a large
   * account shows progress and the connection stays alive. Closing the page closes the stream, and
   * the server stops after the batch in hand.
   */
  async reExtractAllBatches(
    onProgress?: (progress: ReExtractAllProgress) => void
  ): Promise<ReExtractAllResult> {
    const res = await fetch(`${this.baseUrl}/digitization/batch/re-extract-all`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Api-Key': X_API_KEY },
    });
    const totals: ReExtractAllTotals = {
      batches: 0,
      reExtracted: 0,
      failed: 0,
      halted: 0,
      skipped: 0,
    };
    const outcome: { result?: ReExtractAllResult; error?: string } = {};
    let done = 0;
    await this.readNdjson(res, (raw) => {
      const e = raw as unknown as Record<string, any>;
      if (e.type === 'start') {
        totals.batches = e.total ?? 0;
        onProgress?.({ done, total: totals.batches, current: null, totals: { ...totals } });
        return;
      }
      if (typeof e.type === 'string' && e.type.startsWith('batch_') && e.type !== 'batch_start') {
        done++;
        if (e.type === 'batch_done') totals.reExtracted++;
        else if (e.type === 'batch_halted') totals.halted++;
        else if (e.type === 'batch_failed') totals.failed++;
        else if (e.type === 'batch_skipped') totals.skipped++;
        onProgress?.({
          done,
          total: totals.batches,
          current: e.filename ?? null,
          totals: { ...totals },
        });
      } else if (e.type === 'done' || e.type === 'stopped') {
        outcome.result = {
          totals: e.totals ?? totals,
          skippedBy: e.skippedBy ?? {},
          finished: e.type === 'done',
        };
      } else if (e.type === 'error') {
        outcome.error = e.message;
      }
    });
    if (outcome.error) throw new Error(outcome.error);
    if (!outcome.result) {
      throw new Error(
        'The connection closed before the run finished. The batches already done keep their new values; run it again to finish the rest.'
      );
    }
    return outcome.result;
  }

  /**
   * Re-run the checks on every published digitized record (VTK-2192). Streamed over one POST, like
   * re-extract-all: the server sends the running totals after every page of records, which keeps the
   * connection alive however long the run takes. Closing the page stops the server after the page in
   * hand, and pages already written stay written.
   */
  async recountPublishedFindings(
    dryRun: boolean,
    onProgress?: (progress: RecountFindingsProgress) => void
  ): Promise<RecountFindingsResult> {
    const res = await fetch(
      `${this.baseUrl}/digitization/batch/recount-published-findings?dryRun=${dryRun}`,
      { method: 'POST', credentials: 'include', headers: { 'X-Api-Key': X_API_KEY } }
    );
    let total: number | null = null;
    const outcome: { result?: RecountFindingsResult; error?: string } = {};
    await this.readNdjson(res, (raw) => {
      const e = raw as unknown as Record<string, unknown>;
      if (e.type === 'start') {
        total = typeof e.total === 'number' ? e.total : null;
        onProgress?.({ total, totals: recountTotals(e) });
      } else if (e.type === 'progress') {
        onProgress?.({ total, totals: recountTotals(e) });
      } else if (e.type === 'done') {
        outcome.result = { dryRun: e.dryRun === true, totals: recountTotals(e) };
      } else if (e.type === 'error') {
        outcome.error = typeof e.message === 'string' ? e.message : 'Recount failed';
      }
    });
    if (outcome.error) throw new Error(outcome.error);
    if (!outcome.result) {
      throw new Error(
        dryRun
          ? 'The connection closed before the preview finished. Nothing was changed; run it again.'
          : 'The connection closed before the recount finished. Records already recounted keep their new counts; run it again to finish the rest.'
      );
    }
    return outcome.result;
  }

  /** An imported record's own delivered document — it has no pages in a batch PDF (VTK-1991). */
  getBundlePdfUrl(batchId: string, bundleId: string): string {
    return `${this.baseUrl}/digitization/batch/${batchId}/bundles/${bundleId}/pdf`;
  }

  async getOpsReviewQueue(page: PageQuery): Promise<PagedItems<OpsReviewQueueItem>> {
    const res = await this.httpClient.get<PagedItems<OpsReviewQueueItem> | OpsReviewQueueItem[]>(
      `/digitization/batch/ops-review/queue?${pageQueryString(page)}`
    );
    return toPagedItems(res.data, page);
  }

  async getReverificationGaps(): Promise<ReverificationGapsResult> {
    const res = await this.httpClient.get<ReverificationGapsResult>(
      '/digitization/batch/reverification-gaps'
    );
    return res.data ?? { gaps: [], examined: 0, truncated: false };
  }

  async getBundleReviewAudit(batchId: string, bundleId: string): Promise<ReviewAuditRecord[]> {
    const res = await this.httpClient.get<ReviewAuditRecord[]>(
      `/digitization/batch/${batchId}/bundles/${bundleId}/review-audit`
    );
    return res.data ?? [];
  }

  /**
   * The BATCH's own trail — ownership changes and grouping confirmation. Distinct endpoint, not a filter
   * over the bundle route: those events carry no bundleId, so no per-bundle read can return them.
   */
  async getBatchReviewAudit(batchId: string): Promise<ReviewAuditRecord[]> {
    const res = await this.httpClient.get<ReviewAuditRecord[]>(
      `/digitization/batch/${batchId}/review-audit`
    );
    return res.data ?? [];
  }

  /**
   * VTK-1886 — digitize a stored file. Streams NDJSON (analyze + extract) like analyzeBatch, so it
   * uses native fetch (not axios). The endpoint lives on the storage side
   * (POST /i9-storage/files/:id/digitize) because it keeps the source file's status in sync; it
   * creates the batch from the stored blob and emits the new batchId on the `result` event.
   */
  async digitizeStoredFile(fileId: string, onEvent: (e: StreamEvent) => void): Promise<void> {
    const res = await fetch(`${this.baseUrl}/i9-storage/files/${fileId}/digitize`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Api-Key': X_API_KEY },
    });
    await this.readNdjson(res, onEvent);
  }

  // ── Ownership / soft lock (VTK-1883) ──────────────────────────────────────
  async assignBatch(batchId: string, params: AssignBatchParams = {}): Promise<DigitizationBatch> {
    const res = await this.httpClient.post<DigitizationBatch>(
      `/digitization/batch/${batchId}/assign`,
      { assigneeUserId: params.assigneeUserId, takeover: params.takeover ?? false }
    );
    return res.data;
  }

  // Lock the page grouping (VTK-1888) — owner confirms the split; afterwards page reassignment is
  // rejected server-side and the FE makes Grouping non-returnable.
  async lockGrouping(batchId: string): Promise<DigitizationBatch> {
    const res = await this.httpClient.post<DigitizationBatch>(
      `/digitization/batch/${batchId}/lock-grouping`
    );
    return res.data;
  }

  /**
   * Split pages out of a mis-grouped I-9 into a new one in the same batch (VTK-1930). Reassignment can
   * only move a page onto an I-9 that already exists, so a bundle the splitter merged from two
   * employees had nowhere to put the second one. Both sides re-extract.
   */
  async splitBundle(
    batchId: string,
    bundleId: string,
    pages: number[]
  ): Promise<{ source: DigitizationBundle; created: DigitizationBundle }> {
    const res = await this.httpClient.post<{
      source: DigitizationBundle;
      created: DigitizationBundle;
    }>(`/digitization/batch/${batchId}/bundles/${bundleId}/split`, { pages });
    return res.data;
  }

  /**
   * Create a new I-9 from pages that belong to none — the unassigned bucket's way back into a record.
   *
   * Deliberately NOT the split endpoint: that one needs a source I-9 to take the pages off, and these
   * pages are on no I-9, which is why dragging one to "new I-9" used to 404.
   */
  async createBundleFromUnassignedPages(
    batchId: string,
    pages: number[]
  ): Promise<{ created: DigitizationBundle }> {
    const res = await this.httpClient.post<{ created: DigitizationBundle }>(
      `/digitization/batch/${batchId}/bundles/from-unassigned`,
      { pages }
    );
    return res.data;
  }

  /**
   * Publish one record — the explicit per-record publish / retry path (VTK-1888). Completing an I-9
   * auto-publishes it; this is how a failed auto-publish is retried.
   *
   * Returns the outcome: the endpoint answers 200 even when the publish FAILED, putting the real
   * status in `publishResult`. Discarding it (as this once did) reported a failed publish to the user
   * as a success and left them staring at a record that never greyed out.
   */
  async publishBundle(batchId: string, bundleId: string): Promise<PublishBundleResult> {
    const res = await this.httpClient.post<PublishBundleResult>(
      `/digitization/batch/${batchId}/bundles/${bundleId}/publish`
    );
    return res.data;
  }

  async getInProgressQueue(
    params: { assignedTo?: string; accountId?: string } = {}
  ): Promise<DigitizationInProgressQueueItem[]> {
    const qs = new URLSearchParams();
    if (params.assignedTo) qs.set('assignedTo', params.assignedTo);
    if (params.accountId) qs.set('accountId', params.accountId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await this.httpClient.get<DigitizationInProgressQueueItem[]>(
      `/digitization/batch/in-progress/queue${suffix}`
    );
    return res.data ?? [];
  }

  async getInternalUsers(): Promise<InternalUser[]> {
    const res = await this.httpClient.get<InternalUser[]>('/app-users/internal');
    return res.data ?? [];
  }

  async getBatchLocations(batchId: string): Promise<BatchLocation[]> {
    const res = await this.httpClient.get<BatchLocation[]>(
      `/digitization/batch/${batchId}/locations`
    );
    return res.data ?? [];
  }
}

/** The counts a recount event carries; any the server left out read as 0. */
function recountTotals(e: Record<string, unknown>): RecountFindingsTotals {
  const n = (key: string) => (typeof e[key] === 'number' ? (e[key] as number) : 0);
  return {
    examined: n('examined'),
    changed: n('changed'),
    lowered: n('lowered'),
    raised: n('raised'),
    issuesBefore: n('issuesBefore'),
    issuesAfter: n('issuesAfter'),
    nowClean: n('nowClean'),
    skippedCompleted: n('skippedCompleted'),
    casesOpened: n('casesOpened'),
    casesNowEmpty: n('casesNowEmpty'),
    failed: n('failed'),
  };
}
